import { switchMap, catchError, of, combineLatest, type Observable, tap, throwError } from 'rxjs';
import type { RegisterMessageData } from '@dh-fe/react-support-chat';
import authentication from 'services/authentication';
import appLanguage from 'services/i18n/language';
import { Store } from 'store';
import applicationConfig from 'store/application-config';
import geolocation from 'store/geolocation';
import notifyUserOnError from 'store/utils/notify-user-on-error';
import filterWhileNullish from 'utils/rxjs/filter-while-nullish';
import progressiveRetry from 'utils/rxjs/progressive-retry';

import { defaultTopicIdOther } from './online-support-config.settings';

interface OnlineSupportStore {
  onlineSupportConfig: RegisterMessageData | undefined;
  socketUrl: string | undefined;
}

class OnlineSupport extends Store<OnlineSupportStore | undefined> {
  source$ = combineLatest([appLanguage.onChange$, applicationConfig.onChange$, geolocation.onChange$]).pipe(
    tap(() => {
      super.meta.setLoading(true);
    }),

    switchMap(([lang, appConfig, location]) => {
      const ip = location?.ip;
      const country = location?.countryCode;
      const socketUrl = appConfig?.csc.url;

      if (!lang || !ip || !country || !socketUrl) {
        return throwError(() => new Error('missing required lang or location info'));
      }

      return of({
        onlineSupportConfig: {
          country,
          ip,
          lang,
          guest: authentication.isAuthenticated() ? 0 : 1,
          topic: defaultTopicIdOther,
        },
        socketUrl,
      } satisfies OnlineSupportStore);
    }),
    progressiveRetry(),
    notifyUserOnError({ type: 'error', message: 'request-failed', onClick: () => this.reset() }),
    catchError(() => {
      return of<OnlineSupportStore>({
        onlineSupportConfig: undefined,
        socketUrl: undefined,
      });
    })
  );

  constructor() {
    super({ name: 'online-support-config', initialState: undefined });
  }

  get onChange$(): Observable<OnlineSupportStore> {
    return super.onChange$.pipe(filterWhileNullish());
  }
}

export default new OnlineSupport();
export type { OnlineSupportStore };
